import {useQueryClient} from '@tanstack/react-query';
import {useState} from 'react';
import {useCreateStep} from '../../../core/repositories/mypageEntryManagementsRepository';
import type {StepCandidateType} from '../../../core/repositories/mypageEntryManagementsRepository';
import type {StepCreationTriggerType} from '../constants/entrySourceTypes';
import {CACHE_KEYS} from './useEntryManagementUpdate';

export const useStepCandidateSaveActions = () => {
  const queryClient = useQueryClient();
  const createStepMutation = useCreateStep();
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const saveCandidate = async (
    companyName: string | null,
    companyID: number | null | undefined,
    userEntryManagementID: number | undefined,
    candidate: StepCandidateType,
    entrySourceType: StepCreationTriggerType,
  ) => {
    // companyNameが必須
    if (!companyName) {
      setErrorMessage('企業名が指定されていません。');
      return {success: false};
    }

    try {
      // POST /steps でステップを作成
      // companyIDがない場合でもcompanyNameで企業を特定可能
      // stepOrderは指定しない（バックエンドで自動採番）
      const result = await createStepMutation.mutateAsync({
        companyID: companyID ?? undefined,
        companyName,
        stepName: candidate.stepName,
        entrySourceType,
        notes: candidate.notes ?? undefined,
        deadline: candidate.deadline ?? undefined,
        startDate: candidate.startDate ?? undefined,
        endDate: candidate.endDate ?? undefined,
        place: candidate.place ?? undefined,
      });

      if (result.ok) {
        // キャッシュ無効化
        await queryClient.invalidateQueries({
          queryKey: [CACHE_KEYS.PAGINATED_ENTRY_MANAGEMENTS],
        });
        await queryClient.invalidateQueries({
          queryKey: [CACHE_KEYS.ENTRY_MANAGEMENTS],
        });
        if (userEntryManagementID) {
          await queryClient.invalidateQueries({
            queryKey: [CACHE_KEYS.ENTRY_MANAGEMENT, userEntryManagementID],
          });
        }
        // 新規作成された場合も考慮
        if (result.value.userEntryManagementID) {
          await queryClient.invalidateQueries({
            queryKey: [
              CACHE_KEYS.ENTRY_MANAGEMENT,
              result.value.userEntryManagementID,
            ],
          });
        }
        await queryClient.invalidateQueries({
          queryKey: [CACHE_KEYS.PROGRESS_MISSIONS],
        });

        setSuccessMessage('選考予定を保存しました。');
        return {
          success: true,
          stepID: result.value.id,
          userEntryManagementID: result.value.userEntryManagementID,
          googleSyncFailed: result.value.googleSyncResult?.success === false,
        };
      }

      setErrorMessage('選考予定の保存に失敗しました。');
      return {success: false};
    } catch (_error) {
      setErrorMessage('選考予定の保存に失敗しました。');
      return {success: false};
    }
  };

  const clearMessages = () => {
    setSuccessMessage(null);
    setErrorMessage(null);
  };

  return {
    saveCandidate,
    isLoading: createStepMutation.isLoading,
    successMessage,
    errorMessage,
    clearMessages,
  };
};
